import { FullscreenHelper } from '../../helpers/FullscreenHelper';
import { ASSET_TYPE, PRESENTATION_RENDERERS } from '../../constants/Constants';
import { ViewerLayout } from './ViewerLayout';
import { AssetViewerView } from '../views/AssetViewerView';
import { IframePresentationRenderer } from '../renderers/IframePresentationRenderer';
import { AssetViewerPresentationRenderer } from '../renderers/AssetViewerPresentationRenderer';
import { PresentationHelper } from '../../helpers/PresentationHelper';
import { PdfJsPresentationRenderer } from '../renderers/PdfJsPresentationRenderer';
import { ViewerEventType, ViewerViewportChangeEvent } from '../../types/ViewerEvents.type';
import { PdfViewerPresentationRenderer } from '../renderers/PdfViewerPresentationRenderer';
const PAST_CLASS = 'past';
const PRESENT_CLASS = 'present';
const FUTURE_CLASS = 'future';
const SLIDE_CLASS = 'av-slide';
export class LayoutHorizontal extends ViewerLayout {
    constructor(config, eventBus, assetViewerItems, tokenService, pdfJsLinkService) {
        super(config, eventBus, assetViewerItems);
        this.config = config;
        this.eventBus = eventBus;
        this.assetViewerItems = assetViewerItems;
        this.tokenService = tokenService;
        this.pdfJsLinkService = pdfJsLinkService;
        this.isZoomed = false;
        this.currentScale = 1;
        this.lastNavigatedNext = true;
        this.container = null;
        this.views = this.createViews();
        this.onPublicRenderedViewFocus = this.onPublicRenderedViewFocus.bind(this);
        this.onRenderedViewFocus = this.onRenderedViewFocus.bind(this);
        this.addEventListeners();
    }
    set currentView(view) {
        this._currentView = view;
        requestAnimationFrame(() => this.setLayoutClasses());
    }
    get currentView() {
        return this._currentView;
    }
    setLayoutClasses() {
        const currentViewIndex = this.views.indexOf(this.currentView);
        if (currentViewIndex === -1) {
            throw 'no current view index';
        }
        this.views.forEach((view, index) => {
            if (view.container) {
                if (index < currentViewIndex) {
                    view.container.classList.remove(PRESENT_CLASS);
                    view.container.classList.remove(FUTURE_CLASS);
                    view.container.classList.add(PAST_CLASS);
                }
                else if (index === currentViewIndex) {
                    view.container.classList.remove(PAST_CLASS);
                    view.container.classList.remove(FUTURE_CLASS);
                    view.container.classList.add(PRESENT_CLASS);
                }
                else if (index > currentViewIndex) {
                    view.container.classList.remove(PAST_CLASS);
                    view.container.classList.remove(PRESENT_CLASS);
                    view.container.classList.add(FUTURE_CLASS);
                }
            }
        });
        this.eventBus.dispatch(new ViewerViewportChangeEvent({ viewState: this.viewState }));
    }
    get viewState() {
        const view = this.currentView || this.views[0];
        const focussedView = {
            view,
            id: this.views.indexOf(view),
            percent: 100
        };
        return {
            first: focussedView,
            focus: focussedView,
            last: focussedView,
            scroll: {
                down: this.lastNavigatedNext ? true : false
            },
            views: this.views.length ? [focussedView] : []
        };
    }
    render(container) {
        this.container = container;
        this.container.classList.add('av-layout-horizontal');
        this.viewportWidth = this.container.clientWidth;
        this.viewportHeight = this.container.clientHeight;
        this.resetLayoutContainer();
    }
    resetLayoutContainer() {
        this.container.innerHTML = '';
    }
    renderViewContainer(view) {
        const elementIndex = Array.from(this.container.children).indexOf(view.container);
        if (elementIndex === -1) {
            // this will append childs in the rendering order, not in the order of the slides, but for this layout that doesn't matter
            this.calculateLayout();
            // START needed for iFrame rendering of google docs inside chromium browsers (https://jira.showpad.io/browse/SP-27346)
            if (view.assetViewerItem.type === 'url' && view.presentationRenderer instanceof IframePresentationRenderer) {
                view.container.style.width = '0px';
                view.container.style.height = '0px';
                view.container.style.visibility = 'hidden';
            }
            // END needed for iFrame rendering of google docs inside chromium browsers (https://jira.showpad.io/browse/SP-27346)
            this.container.appendChild(view.container);
        }
    }
    destroyViewContainer(view) {
        try {
            this.container.removeChild(view.container);
        }
        catch (e) { }
        try {
            this.container.removeChild(view.contentWrapper);
        }
        catch (e) { }
    }
    setViewSize(view) {
        const contentWrapper = view.contentWrapper;
        const contentElement = view.contentElement;
        if (contentWrapper && contentElement) {
            // No padding for iframes in fullscreen mode
            if (FullscreenHelper.isFullscreen() && view.presentationRenderer instanceof IframePresentationRenderer) {
                view.contentWrapper.style.padding = null;
                view.contentElement.style.width = '100%';
                view.contentElement.style.height = '100%';
            }
            else {
                contentWrapper.style.paddingTop = view.paddingTop + 'px';
                contentWrapper.style.paddingBottom = view.paddingBottom + 'px';
                contentWrapper.style.paddingLeft = view.paddingLeft + 'px';
                contentWrapper.style.paddingRight = view.paddingRight + 'px';
                contentElement.style.width = view.slideWidth + 'px';
                contentElement.style.height = view.slideHeight + 'px';
                contentElement.style.borderWidth = view.border + 'px';
            }
        }
    }
    update(index = null, adjustScrollPosition = false, viewportWidthChanged = false) {
        this.calculateLayout();
    }
    addViews(view, numViews, suggestedWidth, suggestedHeight) {
        const index = this.views.indexOf(view);
        if (index === -1) {
            console.error('view not found in addViews');
            return;
        }
        const viewsToAdd = [];
        for (let i = 0; i < numViews; i++) {
            const page = i + 1;
            const viewToAdd = this.createAssetViewerViewFromAssetViewerItem(view.assetViewerItem, page);
            viewToAdd.contentElement.style.width = suggestedWidth + 'px';
            viewToAdd.contentElement.style.height = suggestedHeight + 'px';
            viewsToAdd.push(viewToAdd);
        }
        this.views = [...this.views.slice(0, index), ...viewsToAdd, ...this.views.slice(index + 1)];
        if (view === this.currentView) {
            this.currentView = viewsToAdd[0];
        }
    }
    createViews() {
        return this.createAssetViewerViewsFromAssetViewerItems(this.assetViewerItems);
    }
    calculateLayout() {
        this.views = this.views.map((view, index) => {
            // const isLastSlide = index === this.views.length - 1
            let border = this.config.VIEW_BORDER;
            let borderBottom = this.config.VIEW_BORDER_BOTTOM;
            let paddingTop = this.config.VIEW_PADDING_TOP;
            let paddingBottom = this.config.VIEW_PADDING_BOTTOM;
            let paddingLeft = this.config.VIEW_PADDING_LEFT;
            let paddingRight = this.config.VIEW_PADDING_RIGHT;
            if (view.presentationRenderer instanceof AssetViewerPresentationRenderer ||
                view.presentationRenderer instanceof PdfViewerPresentationRenderer) {
                paddingTop = paddingBottom = paddingLeft = paddingRight = border = borderBottom = 0;
            }
            else if (view.assetViewerItem.type === ASSET_TYPE.PAGE) {
                paddingTop = paddingBottom = 0;
            }
            const totalBorderWidth = border * 2;
            // Default to the smallest document width, (scaling up looks better than scaling down)
            let maxContentWidth = Math.min(this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth), this.config.MAX_CONTENT_WIDTH);
            let maxContentHeight = this.viewportHeight - (paddingTop + paddingBottom + border + borderBottom);
            if (view.assetViewerItem.type === ASSET_TYPE.DOCUMENT) {
                maxContentWidth = Math.min(this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth), this.config.MAX_CONTENT_WIDTH, this.config.MAX_CONTENT_WIDTH_PORTRAIT_DOCUMENTS);
            }
            else if (view.assetViewerItem.type === ASSET_TYPE.PAGE && this.config.MAX_CONTENT_WIDTH_PAGES) {
                maxContentWidth = Math.min(this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth), this.config.MAX_CONTENT_WIDTH_PAGES);
            }
            if (view.dimensions &&
                !(view.presentationRenderer instanceof IframePresentationRenderer) &&
                !(view.presentationRenderer instanceof AssetViewerPresentationRenderer)) {
                const isLandscape = view.dimensions.width / view.dimensions.height > 1;
                const maxContentWidthConfigValue = view.assetViewerItem.type === ASSET_TYPE.DOCUMENT && !isLandscape
                    ? this.config.MAX_CONTENT_WIDTH_PORTRAIT_DOCUMENTS
                    : this.config.MAX_CONTENT_WIDTH;
                const maxContentWidth = Math.min(this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth), maxContentWidthConfigValue);
                const widthScale = view.dimensions.width / maxContentWidth;
                const scaledHeight = view.dimensions.height / widthScale;
                if ((view.assetViewerItem.type !== ASSET_TYPE.DOCUMENT ||
                    (view.assetViewerItem.type === ASSET_TYPE.DOCUMENT && isLandscape)) &&
                    this.viewportWidth > this.config.LANDSCAPE_FIT_WIDTH_BREAKPOINT_WIDTH &&
                    this.viewportHeight > this.config.LANDSCAPE_FIT_WIDTH_BREAKPOINT_HEIGHT &&
                    scaledHeight > maxContentHeight) {
                    // don"t scale up small images/videos
                    if ((view.assetViewerItem.type === ASSET_TYPE.PHOTO ||
                        view.assetViewerItem.type === ASSET_TYPE.VIDEO ||
                        view.assetViewerItem.type === ASSET_TYPE.MEETING_VIDEO ||
                        view.assetViewerItem.type === ASSET_TYPE.PERSONAL_VIDEO_RECORDING) &&
                        view.dimensions.width < maxContentWidth &&
                        view.dimensions.height < maxContentHeight &&
                        widthScale > 0) {
                        view.slideWidth = view.dimensions.width;
                        view.slideHeight = view.dimensions.height;
                    }
                    else {
                        const heightScale = view.dimensions.height / maxContentHeight;
                        view.slideWidth = view.dimensions.width / heightScale;
                        view.slideHeight = view.dimensions.height / heightScale;
                    }
                }
                else {
                    // don"t scale up small images/videos
                    if ((view.assetViewerItem.type === ASSET_TYPE.PHOTO ||
                        view.assetViewerItem.type === ASSET_TYPE.VIDEO ||
                        view.assetViewerItem.type === ASSET_TYPE.MEETING_VIDEO ||
                        view.assetViewerItem.type === ASSET_TYPE.PERSONAL_VIDEO_RECORDING) &&
                        view.dimensions.width < this.viewportWidth &&
                        widthScale > 0) {
                        view.slideWidth = view.dimensions.width;
                        view.slideHeight = view.dimensions.height;
                    }
                    else {
                        view.slideWidth = view.dimensions.width / widthScale;
                        view.slideHeight = view.dimensions.height / widthScale;
                    }
                }
                // add/remove class for vertical centering
                if (view.slideHeight > this.viewportHeight) {
                    view.contentElement.classList.add('vrow-content-container-exceeds-viewport');
                }
                else if (view.contentElement.classList.contains('vrow-content-container-exceeds-viewport')) {
                    view.contentElement.classList.remove('vrow-content-container-exceeds-viewport');
                }
                // TODO
                // if (view.isZoomed) {
                //     view.slideWidth = view.defaultScaledWidth * view.currentScale;
                //     view.slideHeight = view.defaultScaledHeight * view.currentScale;
                // }
                view.currentScale = view.slideWidth / view.dimensions.width;
            }
            else {
                view.slideWidth = maxContentWidth;
                view.slideHeight = maxContentHeight;
            }
            view.width = view.slideWidth + paddingLeft + paddingRight + totalBorderWidth;
            view.height = view.slideHeight + paddingTop + paddingBottom + borderBottom + totalBorderWidth;
            view.border = border;
            view.borderBottom = borderBottom;
            view.paddingTop = paddingTop;
            view.paddingBottom = paddingBottom;
            view.paddingLeft = paddingLeft;
            view.paddingRight = paddingRight;
            this.setViewSize(view);
            return view;
        });
    }
    addEventListeners() {
        super.addEventListeners();
        this.eventBus.on(ViewerEventType.PublicRenderedViewFocus, this.onPublicRenderedViewFocus);
        this.eventBus.on(ViewerEventType.RenderedViewFocus, this.onRenderedViewFocus);
    }
    removeEventListeners() {
        super.removeEventListeners();
        this.eventBus.off(ViewerEventType.PublicRenderedViewFocus, this.onPublicRenderedViewFocus);
        this.eventBus.off(ViewerEventType.RenderedViewFocus, this.onRenderedViewFocus);
    }
    resize() {
        if (this.container) {
            this.viewportWidth = this.container.clientWidth;
            this.viewportHeight = this.container.clientHeight;
            this.calculateLayout();
        }
    }
    onRenderedViewFocus(event) {
        const { assetViewerView } = event.payload;
        if (assetViewerView && this.views.find(view => view.id === assetViewerView.id)) {
            this.preventTabFocus();
        }
    }
    preventTabFocus() {
        if (!this.currentView) {
            throw 'no current view';
        }
        const currentViewIndex = this.views.indexOf(this.currentView);
        if (currentViewIndex === -1) {
            return;
        }
        this.views.forEach((view, index) => {
            if (view.container && view.presentationRenderer) {
                if (index === currentViewIndex) {
                    if (view.presentationRenderer.audio) {
                        view.presentationRenderer.audio.tabIndex = 0;
                        view.presentationRenderer.audio.style.visibility = 'initial';
                    }
                    else if (view.presentationRenderer.video) {
                        view.presentationRenderer.video.tabIndex = 0;
                        view.presentationRenderer.video.style.visibility = 'initial';
                    }
                    else if (view.presentationRenderer.iframe) {
                        view.presentationRenderer.iframe.tabIndex = 0;
                        view.presentationRenderer.iframe.style.visibility = 'initial';
                    }
                }
                else {
                    if (view.presentationRenderer.audio) {
                        view.presentationRenderer.audio.tabIndex = -1;
                        view.presentationRenderer.audio.style.visibility = 'hidden';
                    }
                    else if (view.presentationRenderer.video) {
                        view.presentationRenderer.video.tabIndex = -1;
                        view.presentationRenderer.video.style.visibility = 'hidden';
                    }
                    else if (view.presentationRenderer.iframe) {
                        view.presentationRenderer.iframe.tabIndex = -1;
                        view.presentationRenderer.iframe.style.visibility = 'hidden';
                    }
                }
            }
        });
    }
    get canZoomIn() {
        return this.config.canZoom && this.currentScale < this.config.MAX_ZOOM_SCALE;
    }
    get canZoomOut() {
        return this.config.canZoom && this.currentScale > this.config.MIN_ZOOM_SCALE;
    }
    zoomIn() {
        if (this.canZoomIn) {
            this.currentScale = this.currentScale + 0.2;
            this.zoom(this.currentScale);
        }
    }
    zoomOut() {
        if (this.canZoomOut) {
            this.currentScale = this.currentScale - 0.2;
            this.zoom(this.currentScale);
        }
    }
    zoom(scale) {
        if (scale === 1) {
            this.resetZoom();
        }
        else {
            this.views.forEach(view => {
                view.isZoomed = true;
                view.currentScale = scale;
                if (view.presentationRenderer instanceof PdfJsPresentationRenderer) {
                    // reset view so canvas can be rerendered
                    view.reset();
                }
            });
            this.isZoomed = true;
            this.update();
        }
    }
    resetZoom() {
        this.isZoomed = null;
        this.currentScale = 1;
        this.views.forEach(view => {
            view.isZoomed = false;
            view.currentScale = null;
            if (view.presentationRenderer instanceof PdfJsPresentationRenderer) {
                // reset view so canvas can be rerendered
                view.reset();
            }
        });
        this.update();
    }
    onPublicRenderedViewFocus(event) {
        const { assetViewerView } = event.payload;
        this.views.forEach(view => {
            var _a, _b;
            if ((_a = view.presentationRenderer) === null || _a === void 0 ? void 0 : _a.video) {
                view.id === assetViewerView.id
                    ? view.presentationRenderer.video.play().then(() => { }, console.warn)
                    : view.presentationRenderer.video.pause();
            }
            if ((_b = view.presentationRenderer) === null || _b === void 0 ? void 0 : _b.audio) {
                view.id === assetViewerView.id
                    ? view.presentationRenderer.audio.play().then(() => { }, console.warn)
                    : view.presentationRenderer.audio.pause();
            }
        });
    }
    createAssetViewerViewFromAssetViewerItem(assetViewerItem, page) {
        const ensuredPage = page || 1;
        const id = assetViewerItem.id + (page ? '-' + parseInt(page) : '');
        const annotations = (assetViewerItem.annotations && assetViewerItem.annotations[ensuredPage]) || null;
        return new AssetViewerView(this.config, this.eventBus, this, assetViewerItem, this.tokenService, this.pdfJsLinkService, id, ensuredPage, annotations);
    }
    createAssetViewerViewsFromAssetViewerItems(assetViewerItems) {
        const views = [];
        assetViewerItems.forEach(assetViewerItem => {
            const isDocument = assetViewerItem.type === ASSET_TYPE.DOCUMENT;
            const isPortrait = isDocument ? PresentationHelper.isDocumentPortrait(assetViewerItem) : false;
            // TODO use constants and make this work for all layouts
            const type = isPortrait && isDocument && this.config.PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP['DOCUMENT_PORTRAIT']
                ? 'DOCUMENT_PORTRAIT'
                : assetViewerItem.type;
            if (this.config.PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP[type] &&
                (this.config.PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP[type][0] === PRESENTATION_RENDERERS.ASSET_VIEWER ||
                    this.config.PRESENTATION_RENDERERS_FOR_ASSET_TYPE_MAP[type][0] === PRESENTATION_RENDERERS.PDF_VIEWER)) {
                views.push(this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, assetViewerItem.page));
            }
            else {
                if (assetViewerItem.pageRanges && assetViewerItem.pageRanges.length) {
                    assetViewerItem.pageRanges.forEach(pageRange => {
                        for (let i = pageRange.start; i <= pageRange.end; i++) {
                            let view = this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, i);
                            views.push(view);
                        }
                    });
                }
                else {
                    for (let i = 0; i < assetViewerItem.numViews; i++) {
                        if (assetViewerItem.numViews > 1) {
                            const page = i + 1;
                            views.push(this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, page));
                        }
                        else {
                            views.push(this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, assetViewerItem.page));
                        }
                    }
                }
            }
        });
        return views;
    }
    createContentWrapper() {
        const element = document.createElement('div');
        element.style.display = 'flex';
        element.style.touchAction = 'manipulation';
        element.style.justifyContent = 'center';
        element.style.width = '100%';
        element.style.height = '100%';
        element.style.overflowY = 'auto';
        // for nested assetViewer with Layout: HORIZONTAL
        element.style.overflowX = 'hidden';
        element.classList.add('vrow-content-container-wrapper');
        return element;
    }
    createContentContainer(view) {
        const element = document.createElement('div');
        element.classList.add('vrow-content-container');
        element.classList.add(`vrow-content-container-${view.assetViewerItem.type}`);
        element.classList.add(`vrow-content-container-vertical-center`);
        element.classList.add('loading');
        element.style.position = 'relative';
        element.style.borderWidth = '1px';
        element.style.boxSizing = 'content-box';
        element.style.flex = '0 0 auto'; // otherwise flexbox doesn't respect the fixed width and height set on the element
        // Fucks up border:
        // element.style.transform = "translateZ(0)";
        if (this.config.TRANSITIONS_ENABLED) {
            // (<any>element.style).willChange = "opacity"; // issue with video's in Safari in fullscreen
            element.style.transition = 'opacity .5s ease-in-out';
        }
        return element;
    }
    createContainer() {
        const element = document.createElement('div');
        element.classList.add(SLIDE_CLASS);
        // Make sure that newly rendered views do not appear in the viewport until a new currentView is set
        element.classList.add(FUTURE_CLASS);
        return element;
    }
}
//# sourceMappingURL=LayoutHorizontal.js.map